import { useEffect, useRef } from "react";
import { isAxiosError } from "axios";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FaSpinner, FaCheck, FaXmark } from "react-icons/fa6";
import { css } from "../../../../styled-system/css";
import { Flex, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { useSpApiOAuthExchange } from "../../../modules/onboarding/amazon/useSpApiOAuthExchange";
import { decodeSpApiOAuthState } from "../../../modules/onboarding/amazon/initSpApiOAuth";
import { AMAZON_ONBOARDING_STATUS_QUERY_KEY } from "../../../modules/onboarding/amazon/useAmazonOnboardingStatus";
import { getSameOriginPath } from "../../../utils/redirect";

const DEFAULT_SUCCESS_PATH = "/onboarding/v2/connect";
const DEFAULT_ERROR_PATH = "/";

function SpApiOAuthCallback() {
  const { t } = useTranslation(["onboarding"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const exchangeMutation = useSpApiOAuthExchange();
  const hasInitiated = useRef(false);

  const params = new URLSearchParams(window.location.search);
  const code = params.get("spapi_oauth_code");
  const sellingPartnerId = params.get("selling_partner_id");
  const stateParam = params.get("state");

  const decodedState = decodeSpApiOAuthState(stateParam);
  // `state` comes back in the callback URL, so anyone can forge it: only follow same-origin paths.
  const successPath = getSameOriginPath(decodedState?.successPath, DEFAULT_SUCCESS_PATH);
  const errorPath = getSameOriginPath(decodedState?.errorPath, DEFAULT_ERROR_PATH);
  const region = decodedState?.region;

  const hasCode = Boolean(code);
  const hasSellingPartnerId = Boolean(sellingPartnerId);
  const hasRegion = Boolean(region);
  const validationError =
    !hasCode ? t(($) => $.onboarding["Invalid callback URL: missing authorization code"])
    : !hasSellingPartnerId ? t(($) => $.onboarding["Invalid callback URL: missing selling partner ID"])
    : !hasRegion ? t(($) => $.onboarding["Invalid callback URL: missing region information"])
    : null;

  useEffect(() => {
    if (
      !code ||
      !sellingPartnerId ||
      !region ||
      hasInitiated.current ||
      exchangeMutation.isPending ||
      exchangeMutation.isSuccess
    ) {
      return;
    }

    hasInitiated.current = true;

    exchangeMutation.mutate(
      {
        spapi_oauth_code: code,
        selling_partner_id: sellingPartnerId,
        region,
      },
      {
        onSuccess: () => {
          // Invalidate caches so other components fetch fresh data
          void queryClient.invalidateQueries({ queryKey: AMAZON_ONBOARDING_STATUS_QUERY_KEY });
          // The credential check never goes stale on its own, so the SP
          // banners would otherwise outlive the re-authorization.
          void queryClient.invalidateQueries({ queryKey: ["spApiCredentialCheck"] });

          void navigate({ href: successPath });
        },
      },
    );
  }, [code, sellingPartnerId, region, exchangeMutation, queryClient, navigate, successPath]);

  const handleGoBack = () => {
    void navigate({ href: errorPath });
  };

  const errorMessage =
    validationError ||
    (isAxiosError(exchangeMutation.error) && typeof exchangeMutation.error.response?.data === "string" ?
      exchangeMutation.error.response.data
    : null) ||
    (exchangeMutation.error instanceof Error ? exchangeMutation.error.message : null) ||
    (exchangeMutation.isError ? t(($) => $.onboarding["Failed to connect Amazon SP-API account"]) : "");

  const hasError = validationError || exchangeMutation.isError;

  return (
    <Flex
      role="status"
      aria-live="polite"
      aria-atomic="true"
      gap="small"
      direction="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      padding="large"
    >
      {(exchangeMutation.isPending || (exchangeMutation.isIdle && !validationError)) && (
        <>
          <div
            className={css({
              color: "primary",
              fontSize: "xxlarge",
              animation: "spin 1s linear infinite",
            })}
            role="img"
            aria-label={t(($) => $.onboarding["Loading icon"])}
          >
            <FaSpinner />
          </div>
          <div className={css({ fontSize: "large", fontWeight: "bold" })}>
            {t(($) => $.onboarding["Connecting Amazon SP-API..."])}
          </div>
          <div className={css({ color: "gray.600", textAlign: "center" })}>
            {t(($) => $.onboarding["Please wait while we connect your Amazon Seller/Vendor account."])}
          </div>
        </>
      )}

      {exchangeMutation.isSuccess && (
        <>
          <div
            className={css({ color: "success", fontSize: "xxlarge" })}
            role="img"
            aria-label={t(($) => $.onboarding["Success icon"])}
          >
            <FaCheck />
          </div>
          <div className={css({ fontSize: "large", fontWeight: "bold" })}>
            {t(($) => $.onboarding["Successfully Connected!"])}
          </div>
          <div className={css({ color: "gray.600", textAlign: "center" })}>
            {t(($) => $.onboarding["Redirecting..."])}
          </div>
        </>
      )}

      {hasError && (
        <VStack gap="medium" alignItems="center" maxWidth="500px">
          <div
            className={css({ color: "error", fontSize: "xxlarge" })}
            role="img"
            aria-label={t(($) => $.onboarding["Error icon"])}
          >
            <FaXmark />
          </div>
          <div className={css({ fontSize: "large", fontWeight: "bold" })}>
            {t(($) => $.onboarding["Connection Failed"])}
          </div>
          <div className={css({ color: "gray.700", textAlign: "center" })} role="alert">
            {errorMessage}
          </div>
          <Flex gap="medium" marginTop="medium">
            <Button variant="secondary" onPress={handleGoBack}>
              {t(($) => $.onboarding["Go Back"])}
            </Button>
          </Flex>
        </VStack>
      )}
    </Flex>
  );
}

export const Route = createFileRoute("/oauth/amazon/spapi")({
  component: SpApiOAuthCallback,
});
